import { useCallback, useEffect, useMemo, useState } from 'react';
import { History, Lock, NotebookPen, Pencil, Plus, Star, Trash2 } from 'lucide-react';
import { Card, CardContent } from '../ui/Card';
import Button from '../ui/Button';
import StaffRecordFiles from './StaffRecordFiles';
import staffRecordService, { errorText } from '../../services/staffRecords';
import {
  NOTE_CATEGORIES, NOTE_CATEGORY_LABEL,
  type NoteCategory, type StaffFile, type StaffNote, type StaffNoteInput, type StaffNoteRevision, type StaffRecordFile,
} from '../../types/staffRecords';
import { formatDateTimeInTz, formatLocalDateInTz, getTodayInTz, useOrgTimezone } from '../../utils/timezone';

const inputClass = 'w-full min-w-0 border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500';

/**
 * File notes on one employee, on the staff profile (Timesheet HR seat): a
 * resignation, a conversation, something important to know about them.
 * Notes marked important are pinned at the top. An edit keeps the old text;
 * a note is removed with a reason, never deleted. The employee never sees them.
 */
export default function StaffNotesPanel({ userId, userName }: { userId: number; userName: string }) {
  const tz = useOrgTimezone();
  const [file, setFile] = useState<StaffFile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<number | null>(null);
  const [category, setCategory] = useState<NoteCategory | ''>('');
  const [showRemoved, setShowRemoved] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setFile(await staffRecordService.getFile(userId));
    } catch (err) {
      setError(errorText(err, 'Could not load this person\'s file notes.'));
    }
  }, [userId]);

  useEffect(() => { setFile(null); load(); }, [load]);

  const notes = useMemo(() => file?.notes ?? [], [file]);
  const important = notes.filter(n => n.important && !n.removed_at);
  const shown = useMemo(() => notes.filter(n =>
    (showRemoved || !n.removed_at) && (!category || n.category === category)
  ), [notes, showRemoved, category]);
  const removedCount = notes.filter(n => n.removed_at).length;

  const filesOf = (noteId: number) => (file?.files ?? []).filter(f => f.note_id === noteId);

  return (
    <div className="space-y-6">
      {important.length > 0 && (
        <Card>
          <CardContent className="pt-6 space-y-3">
            <h3 className="text-base font-semibold text-neutral-900 flex items-center gap-2">
              <Star className="h-5 w-5 text-amber-500 shrink-0" /> Important to know about {userName}
            </h3>
            <ul className="space-y-2">
              {important.map(n => (
                <li key={n.id} className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-950 min-w-0">
                  {n.title && <p className="font-semibold break-words">{n.title}</p>}
                  <p className="whitespace-pre-wrap break-words">{n.body}</p>
                  <p className="text-xs text-amber-800 mt-1">
                    {NOTE_CATEGORY_LABEL[n.category]} · {formatLocalDateInTz(n.note_date, tz)}
                  </p>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3 min-w-0">
              <NotebookPen className="h-5 w-5 text-primary-600 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-neutral-900">File notes</h3>
                <p className="text-sm text-neutral-500 flex items-center gap-1">
                  <Lock className="h-3.5 w-3.5 shrink-0" />
                  Only people with a Timesheet HR seat can see these. {userName} cannot.
                </p>
              </div>
            </div>
            {!adding && (
              <Button size="sm" onClick={() => { setAdding(true); setEditing(null); }} className="flex items-center gap-1">
                <Plus className="h-4 w-4" /> Add a note
              </Button>
            )}
          </div>

          {adding && (
            <NoteForm
              tz={tz}
              onCancel={() => setAdding(false)}
              onSave={async (input, files) => {
                const note = await staffRecordService.addNote(userId, input);
                for (const f of files) await staffRecordService.attach('notes', note.id, f);
                setAdding(false);
                await load();
              }}
            />
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}
          {!file && !error && <p className="text-sm text-neutral-500">Loading…</p>}

          {file && notes.length > 0 && (
            <div className="flex items-center gap-3 flex-wrap">
              <label htmlFor="note-category-filter" className="sr-only">Show notes of one kind</label>
              <select
                id="note-category-filter"
                className="border border-neutral-300 rounded-lg px-3 py-1.5 text-sm w-full sm:w-auto"
                value={category}
                onChange={e => setCategory(e.target.value as NoteCategory | '')}
              >
                <option value="">Every kind of note</option>
                {NOTE_CATEGORIES.map(c => <option key={c} value={c}>{NOTE_CATEGORY_LABEL[c]}</option>)}
              </select>
              {removedCount > 0 && (
                <label className="flex items-center gap-2 text-sm text-neutral-600 cursor-pointer select-none">
                  <input type="checkbox" checked={showRemoved} onChange={e => setShowRemoved(e.target.checked)}
                    className="h-4 w-4 rounded border-neutral-300 text-primary-600" />
                  Show removed notes ({removedCount})
                </label>
              )}
            </div>
          )}

          {file && notes.length === 0 && !adding && (
            <p className="text-sm text-neutral-500">No file notes on {userName} yet.</p>
          )}
          {file && notes.length > 0 && shown.length === 0 && (
            <p className="text-sm text-neutral-500">No notes of that kind.</p>
          )}

          <ul className="space-y-3">
            {shown.map(n => (
              <li key={n.id}>
                {editing === n.id ? (
                  <NoteForm
                    tz={tz}
                    initial={n}
                    onCancel={() => setEditing(null)}
                    onSave={async (input) => {
                      await staffRecordService.editNote(n.id, input);
                      setEditing(null);
                      await load();
                    }}
                  />
                ) : (
                  <NoteCard
                    note={n}
                    tz={tz}
                    files={filesOf(n.id)}
                    onEdit={() => { setEditing(n.id); setAdding(false); }}
                    onChanged={load}
                  />
                )}
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

function NoteCard({ note, tz, files, onEdit, onChanged }: {
  note: StaffNote;
  tz: string;
  files: StaffRecordFile[];
  onEdit: () => void;
  onChanged: () => void;
}) {
  const [removing, setRemoving] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<StaffNoteRevision[] | null>(null);
  const removed = !!note.removed_at;

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await staffRecordService.removeNote(note.id, reason.trim());
      setRemoving(false);
      onChanged();
    } catch (err) {
      setError(errorText(err, 'The note could not be removed'));
    } finally {
      setBusy(false);
    }
  };

  const toggleHistory = async () => {
    if (history) { setHistory(null); return; }
    try {
      setHistory(await staffRecordService.noteRevisions(note.id));
    } catch (err) {
      setError(errorText(err, 'Could not load the earlier versions'));
    }
  };

  return (
    <div className={`border rounded-lg p-4 space-y-2 min-w-0 ${removed ? 'border-neutral-200 bg-neutral-50 opacity-75' : 'border-neutral-200'}`}>
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <span className="font-medium text-neutral-700">{formatLocalDateInTz(note.note_date, tz)}</span>
        <span className="px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-700">{NOTE_CATEGORY_LABEL[note.category]}</span>
        {note.important && (
          <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 flex items-center gap-1">
            <Star className="h-3 w-3" /> Important
          </span>
        )}
        {removed && <span className="px-2 py-0.5 rounded-full bg-neutral-200 text-neutral-600">Removed</span>}
      </div>
      {note.title && <p className="font-semibold text-neutral-900 break-words">{note.title}</p>}
      <p className={`text-sm text-neutral-800 whitespace-pre-wrap break-words ${removed ? 'line-through' : ''}`}>{note.body}</p>
      <p className="text-xs text-neutral-500 break-words">
        Written by {note.created_by_name || 'someone'}, {formatDateTimeInTz(note.created_at, tz)}
        {note.updated_at && <> · edited by {note.updated_by_name || 'someone'}, {formatDateTimeInTz(note.updated_at, tz)}</>}
      </p>
      {removed && (
        <p className="text-xs text-neutral-600 break-words">
          Removed by {note.removed_by_name || 'someone'}{note.removed_at && `, ${formatDateTimeInTz(note.removed_at, tz)}`}: {note.removed_reason}
        </p>
      )}

      <StaffRecordFiles kind="notes" ownerId={note.id} files={files} onChanged={onChanged} readOnly={removed} />

      {!removed && !removing && (
        <div className="flex items-center gap-4 flex-wrap pt-1 text-sm">
          <button type="button" onClick={onEdit} className="flex items-center gap-1 text-primary-700 hover:underline">
            <Pencil className="h-3.5 w-3.5" /> Edit
          </button>
          <button type="button" onClick={() => setRemoving(true)} className="flex items-center gap-1 text-red-600 hover:underline">
            <Trash2 className="h-3.5 w-3.5" /> Remove
          </button>
          {note.revisions > 0 && (
            <button type="button" onClick={toggleHistory} className="flex items-center gap-1 text-neutral-600 hover:underline">
              <History className="h-3.5 w-3.5" /> {history ? 'Hide' : 'Show'} earlier versions ({note.revisions})
            </button>
          )}
        </div>
      )}
      {removed && note.revisions > 0 && (
        <button type="button" onClick={toggleHistory} className="flex items-center gap-1 text-sm text-neutral-600 hover:underline">
          <History className="h-3.5 w-3.5" /> {history ? 'Hide' : 'Show'} earlier versions ({note.revisions})
        </button>
      )}

      {removing && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg space-y-2">
          <label htmlFor={`remove-reason-${note.id}`} className="block text-sm font-medium text-red-800">
            Why is this note being removed?
          </label>
          <input id={`remove-reason-${note.id}`} className={inputClass} value={reason}
            onChange={e => setReason(e.target.value)} placeholder="For example: written on the wrong person" />
          <p className="text-xs text-red-700">It stays on the file, crossed out, with your reason.</p>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="danger" disabled={busy || reason.trim().length < 3} onClick={remove}>
              {busy ? 'Removing…' : 'Remove note'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setRemoving(false); setReason(''); }}>Cancel</Button>
          </div>
        </div>
      )}

      {history && (
        <div className="border-t border-neutral-200 pt-2 space-y-2">
          {history.length === 0 && <p className="text-sm text-neutral-500">No earlier versions.</p>}
          {history.map(v => (
            <div key={v.id} className="text-sm bg-neutral-50 rounded-lg p-3 min-w-0">
              <p className="text-xs text-neutral-500 break-words">
                As written by {v.written_by_name || 'someone'}, {formatDateTimeInTz(v.written_at, tz)};
                replaced by {v.replaced_by_name || 'someone'}, {formatDateTimeInTz(v.replaced_at, tz)}
              </p>
              <p className="text-xs text-neutral-600">
                {formatLocalDateInTz(v.note_date, tz)} · {NOTE_CATEGORY_LABEL[v.category]}{v.important ? ' · Important' : ''}
              </p>
              {v.title && <p className="font-semibold break-words">{v.title}</p>}
              <p className="whitespace-pre-wrap break-words">{v.body}</p>
            </div>
          ))}
        </div>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
}

function NoteForm({ tz, initial, onSave, onCancel }: {
  tz: string;
  initial?: StaffNote;
  onSave: (input: StaffNoteInput, files: File[]) => Promise<void>;
  onCancel: () => void;
}) {
  const [category, setCategory] = useState<NoteCategory>(initial?.category ?? 'general');
  const [noteDate, setNoteDate] = useState(initial?.note_date ?? getTodayInTz(tz));
  const [title, setTitle] = useState(initial?.title ?? '');
  const [body, setBody] = useState(initial?.body ?? '');
  const [important, setImportant] = useState(initial?.important ?? false);
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const idp = initial ? `note-${initial.id}` : 'note-new';

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSave({ category, note_date: noteDate, title: title.trim() || null, body: body.trim(), important }, files);
    } catch (err) {
      setError(errorText(err, 'The note could not be saved'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-primary-200 bg-primary-50/40 rounded-lg p-4 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="min-w-0">
          <label htmlFor={`${idp}-date`} className="block text-sm font-medium text-neutral-700 mb-1">Date</label>
          <input id={`${idp}-date`} type="date" className={inputClass} value={noteDate} onChange={e => setNoteDate(e.target.value)} />
        </div>
        <div className="min-w-0">
          <label htmlFor={`${idp}-category`} className="block text-sm font-medium text-neutral-700 mb-1">Kind of note</label>
          <select id={`${idp}-category`} className={inputClass} value={category} onChange={e => setCategory(e.target.value as NoteCategory)}>
            {NOTE_CATEGORIES.map(c => <option key={c} value={c}>{NOTE_CATEGORY_LABEL[c]}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label htmlFor={`${idp}-title`} className="block text-sm font-medium text-neutral-700 mb-1">Heading (optional)</label>
        <input id={`${idp}-title`} className={inputClass} value={title} maxLength={200} onChange={e => setTitle(e.target.value)}
          placeholder="For example: Resigned, last day 2 November" />
      </div>
      <div>
        <label htmlFor={`${idp}-body`} className="block text-sm font-medium text-neutral-700 mb-1">Note</label>
        <textarea id={`${idp}-body`} rows={5} className={inputClass} value={body} onChange={e => setBody(e.target.value)}
          placeholder="What happened, who was there, what was said and agreed." />
      </div>
      <label className="flex items-start gap-2 text-sm text-neutral-700 cursor-pointer select-none">
        <input type="checkbox" checked={important} onChange={e => setImportant(e.target.checked)}
          className="h-4 w-4 mt-0.5 rounded border-neutral-300 text-primary-600" />
        <span>Important: pin it at the top of their file (an allergy, a restriction, how to reach their family)</span>
      </label>
      {!initial && (
        <div>
          <label htmlFor={`${idp}-files`} className="block text-sm font-medium text-neutral-700 mb-1">Attach files (optional)</label>
          <input id={`${idp}-files`} type="file" multiple accept="image/*,.pdf,.doc,.docx,.txt,.eml,.msg"
            className="block w-full min-w-0 text-sm text-neutral-600"
            onChange={e => setFiles(Array.from(e.target.files ?? []))} />
          <p className="text-xs text-neutral-500 mt-1">A resignation letter, a photo of a signed form. Up to 15 MB each.</p>
        </div>
      )}
      {initial && <p className="text-xs text-neutral-500">The note as it reads now is kept as an earlier version.</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex gap-2 flex-wrap">
        <Button size="sm" disabled={busy || !body.trim() || !noteDate} onClick={save}>
          {busy ? 'Saving…' : initial ? 'Save changes' : 'Save note'}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel} disabled={busy}>Cancel</Button>
      </div>
    </div>
  );
}
